import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiFolder, FiEye, FiUsers, FiCalendar } from 'react-icons/fi';
import { foldersAPI } from '../services/api';
import { toast } from 'react-toastify';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 20px;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 32px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const Header = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const FolderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
`;

const FolderIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, ${props => props.color || '#0ea5e9'} 0%, ${props => props.color || '#0284c7'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
`;

const FolderInfo = styled.div`
  flex: 1;
`;

const FolderTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
`;

const FolderDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const FolderMeta = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const MetaValue = styled.span`
  background: linear-gradient(135deg, ${props => props.color || '#0ea5e9'} 0%, ${props => props.color || '#0284c7'} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const FormsSection = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }
`;

const FormTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const FormDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px 0;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const FormMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #9ca3af;
`;

const ViewButton = styled.button`
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
`;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #dc2626;
  margin: 0 0 8px 0;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
`;

function SharedFolder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [folder, setFolder] = useState(null);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFolder();
  }, [id]);

  const loadFolder = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await foldersAPI.getSharedFolder(id);
      
      setFolder(response.data.data.folder);
      setForms(response.data.data.forms);
    } catch (err) {
      console.error('Error loading folder:', err);
      setError('Failed to load folder');
      toast.error('Failed to load folder');
    } finally {
      setLoading(false);
    }
  };

  const handleFormClick = (form) => {
    const formUrl = form.custom_url 
      ? `/form/${form.custom_url}`
      : `/form/${form.id}`;
    window.open(formUrl, '_blank');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container>
        <Content>
          <LoadingContainer>
            <LoadingSpinner />
            <p>Loading folder...</p>
          </LoadingContainer>
        </Content>
      </Container>
    );
  }

  if (error || !folder) {
    return (
      <Container>
        <Content>
          <ErrorContainer>
            <ErrorTitle>Folder Not Found</ErrorTitle>
            <ErrorMessage>
              The folder you're looking for doesn't exist or has been removed.
            </ErrorMessage>
            <BackButton onClick={() => navigate('/')}>
              <FiArrowLeft />
              Go Back Home
            </BackButton>
          </ErrorContainer>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <BackButton onClick={() => navigate('/')}>
          <FiArrowLeft />
          Back to Home
        </BackButton>

        <Header>
          <FolderHeader>
            <FolderIcon color={folder.color}>
              <FiFolder />
            </FolderIcon>
            <FolderInfo>
              <FolderTitle>{folder.name}</FolderTitle>
              <FolderDescription>
                {folder.description || 'No description provided'}
              </FolderDescription>
              <FolderMeta>
                <MetaItem>
                  <FiUsers />
                  <MetaValue color={folder.color}>{forms.length}</MetaValue>
                  <span>forms</span>
                </MetaItem>
                <MetaItem>
                  <FiCalendar />
                  <span>Created by {folder.created_by_username}</span>
                </MetaItem>
                <MetaItem>
                  <FiCalendar />
                  <span>Created {formatDate(folder.created_at)}</span>
                </MetaItem>
              </FolderMeta>
            </FolderInfo>
          </FolderHeader>
        </Header>

        <FormsSection>
          <SectionTitle>
            <FiFolder />
            Forms in this Folder ({forms.length})
          </SectionTitle>

          {forms.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#6b7280'
            }}>
              <FiFolder size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>This folder is empty</p>
            </div>
          ) : (
            <FormsGrid>
              {forms.map((form) => (
                <FormCard key={form.id} onClick={() => handleFormClick(form)}>
                  <FormTitle>{form.title}</FormTitle>
                  <FormDescription>
                    {form.description || 'No description provided'}
                  </FormDescription>
                  <FormMeta>
                    <span>Created {formatDate(form.created_at)}</span>
                    <ViewButton>
                      <FiEye />
                      View Form
                    </ViewButton>
                  </FormMeta>
                </FormCard>
              ))}
            </FormsGrid>
          )}
        </FormsSection>
      </Content>
    </Container>
  );
}

export default SharedFolder;
